import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getCodiciSconto, getDatiAzienda } from '../services/api';
import { Spinner } from 'react-bootstrap';
import '../print.css';

function CodiciScontoPrint() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [codici, setCodici] = useState([]);
  const [datiAzienda, setDatiAzienda] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const printTriggered = useRef(false);
  const loadedImagesCount = useRef(0);

  useEffect(() => {
    loadCodici();
  }, []);

  useEffect(() => {
    const handleAfterPrint = () => {
      setTimeout(() => {
        navigate('/codici-sconto');
      }, 100);
    };

    if (!loading && codici.length > 0 && datiAzienda && imagesLoaded && !printTriggered.current) {
      printTriggered.current = true;
      window.addEventListener('afterprint', handleAfterPrint);
      // Piccolo delay per assicurarsi che tutto sia renderizzato
      setTimeout(() => {
        window.print();
      }, 300);
    }

    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [loading, codici.length, datiAzienda, imagesLoaded, navigate]);

  const loadCodici = async () => {
    try {
      const idsParam = searchParams.get('ids');
      if (!idsParam) {
        alert('Nessun codice selezionato');
        navigate('/codici-sconto');
        return;
      }

      const ids = idsParam.split(',').map(id => parseInt(id));
      const [codiciResponse, aziendaResponse] = await Promise.all([
        getCodiciSconto(0, 1000),
        getDatiAzienda()
      ]);
      
      const allCodici = codiciResponse.data.codici_sconto || [];
      const selectedCodici = allCodici.filter(c => ids.includes(c.id));
      
      setCodici(selectedCodici);
      setDatiAzienda(aziendaResponse.data);
      
      // Se non c'è logo, imposta subito imagesLoaded a true
      if (!aziendaResponse.data?.logo_url) {
        setImagesLoaded(true);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Errore caricamento codici:', error);
      alert('Errore nel caricamento dei codici');
      setLoading(false);
    }
  };
  const handleImageLoad = () => {
    loadedImagesCount.current += 1;
    // Tutte le immagini caricate (una per coupon se c'è logo)
    if (loadedImagesCount.current >= codici.length) {
      setImagesLoaded(true);
    }
  };
  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p>Preparazione stampa codici sconto...</p>
      </div>
    );
  }

  if (codici.length === 0) {
    return (
      <div className="text-center p-5">
        <h3>Nessun codice trovato</h3>
        <button onClick={() => navigate('/codici-sconto')} className="btn btn-primary mt-3">
          Torna alla Lista
        </button>
      </div>
    );
  }

  // Raggruppa i codici in blocchi di 6 (una pagina)
  const chunkSize = 6;
  const pages = [];
  for (let i = 0; i < codici.length; i += chunkSize) {
    pages.push(codici.slice(i, i + chunkSize));
  }

  return (
    <div id="printable-area" className="print-container">
      <style>{`
        @page {
          size: A4;
          margin: 8mm;
        }

        .print-page {
          page-break-after: always;
          min-height: 277mm;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .print-page:last-child {
          page-break-after: auto;
        }

        .coupons-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-template-rows: repeat(3, 1fr);
          gap: 8mm;
          width: 100%;
          max-width: 165mm;
          margin: 0 auto;
        }

        .coupon-wrapper {
          position: relative;
          padding: 1.5mm;
        }

        .coupon-wrapper::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border: 1px dashed #999;
          pointer-events: none;
        }

        .coupon {
          width: 100%;
          aspect-ratio: 1;
          border: 2px solid #0066cc;
          border-radius: 6px;
          padding: 5mm;
          background: white;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          box-sizing: border-box;
          page-break-inside: avoid;
          break-inside: avoid;
          position: relative;
        }

        .coupon-logo {
          text-align: center;
          margin-bottom: 3px;
          padding-bottom: 3px;
          min-height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .coupon-logo img {
          max-height: 28px;
          max-width: 100%;
          object-fit: contain;
        }

        .coupon-logo-text {
          font-size: 14px;
          font-weight: bold;
          color: #0066cc;
        }

        .coupon-website {
          text-align: center;
          font-size: 11px;
          color: #0066cc;
          font-weight: 600;
          margin-bottom: 4px;
          padding-bottom: 4px;
          border-bottom: 1.5px solid #0066cc;
        }

        .coupon-type {
          text-align: center;
          font-size: 13px;
          color: #333;
          margin-bottom: 6px;
          font-weight: 700;
        }

        .coupon-discount {
          text-align: center;
          font-size: 36px;
          font-weight: bold;
          color: #0066cc;
          margin: 6px 0;
          line-height: 1;
        }

        .coupon-code-section {
          text-align: center;
          margin: 6px 0;
        }

        .coupon-code-label {
          font-size: 9px;
          color: #666;
          margin-bottom: 3px;
        }

        .coupon-code-value {
          font-size: 13px;
          font-weight: bold;
          font-family: 'Courier New', monospace;
          color: #000;
          background: #f5f5f5;
          padding: 4px 8px;
          border-radius: 3px;
          border: 1px dashed #999;
          display: inline-block;
          word-break: break-all;
        }

        .coupon-expiry {
          text-align: center;
          font-size: 9px;
          color: #666;
          margin-top: 5px;
          border-top: 1px solid #ddd;
          padding-top: 4px;
        }

        @media print {
          @page {
            size: A4;
            margin: 8mm;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .coupon-logo img {
            display: block !important;
            visibility: visible !important;
            -webkit-print-color-adjust: exact !important;
          }

          .print-page {
            min-height: 277mm;
          }

          .coupons-grid {
            gap: 6mm;
          }
        }
      `}</style>

      {pages.map((pageCodeici, pageIndex) => (
        <div key={pageIndex} className="print-page">
          <div className="coupons-grid">
            {pageCodeici.map(codice => (
              <div key={codice.id} className="coupon-wrapper">
                <div className="coupon">
              <div>
                <div className="coupon-logo">
                  {datiAzienda?.logo_url ? (
                    <img 
                      src={`${process.env.REACT_APP_STATIC_FILES_BASE_URL}${datiAzienda.logo_url}`} 
                      alt={datiAzienda.denominazione || 'Logo'}
                      onLoad={handleImageLoad}
                      onError={handleImageLoad}
                    />
                  ) : (
                    <span className="coupon-logo-text">{datiAzienda?.denominazione || 'MODENA CINEMA'}</span>
                  )}
                </div>
                <div className="coupon-website">
                  {datiAzienda?.home_page || 'www.modenacinema.it'}
                </div>
                <div className="coupon-type">
                  {codice.tipo === 'PRIMO_ACQUISTO' ? 'Sconto Primo Acquisto' : 'Buono Sconto'}
                </div>
              </div>

              <div className="coupon-discount">
                {codice.percentuale_sconto}%
              </div>

              <div>
                <div className="coupon-code-section">
                  <div className="coupon-code-label">Codice Sconto:</div>
                  <div className="coupon-code-value">{codice.codice}</div>
                </div>

                {codice.valido_fino && (
                  <div className="coupon-expiry">
                    ⏰ Valido fino al {new Date(codice.valido_fino).toLocaleDateString('it-IT')}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
        </div>
      ))}
    </div>
  );
}

export default CodiciScontoPrint;
